import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { INSURANCE_CONVENTION_ITEM_TYPES_QUERY, ITEM_TYPES_DICTIONARY_QUERY } from 'features/insuranceConventions/edit/queries';
import { useApolloClient } from '@apollo/client';
import { Grid, Box, Icon, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Button, FakeText, Typography } from '@totalsoft/rocket-ui';
import { sortBy } from 'utils/functions';
import {
  ADD_UPDATE_INSURANCE_CONVENTION_ITEM_TYPE,
  DELETE_INSURANCE_CONVENTION_ITEM_TYPE
} from 'features/insuranceConventions/edit/mutations';
import {
  INSURANCE_CONVENTION_ITEM_TYPE_DELETED,
  INSURANCE_CONVENTION_ITEM_TYPE_UPDATED
} from 'features/insuranceConventions/edit/subscriptions';
import { StyledTable, StyledHeaderCell, StyledGrayIcon } from 'components/common/Styles';
import InsuranceConventionItemTypeContainer from './InsuranceConventionItemTypeContainer';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';

const InsuranceConventionItemTypesContainer = ({ productId }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const [itemTypes, setItemTypes] = useState([]);
  const [inAdd, setInAdd] = useState(false);
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const isNewConvention = useMemo(() => !productId, [productId]);

  const { loading } = useQuery(INSURANCE_CONVENTION_ITEM_TYPES_QUERY, {
    variables: { productId },
    skip: !productId,
    onCompleted: data => {
      const newItemTypes = (data?.insuranceConventionItemTypes || []).map(x => ({
        ...x,
        disabled: false,
        editing: false,
        isNew: false
      }));
      setItemTypes(newItemTypes);
    }
  });

  const { data: itemTypesData } = useQuery(ITEM_TYPES_DICTIONARY_QUERY);

  const itemTypesDictionary = useMemo(() => itemTypesData?.itemTypes || [], [itemTypesData]);

  const updateLocalItemType = useCallback(
    (itemTypeId, property, value) => {
      setItemTypes(prev => {
        const modifyItemType = itemType => {
          const newItemType = { ...itemType };
          newItemType[property] = value;
          if (property === 'editing' && !value) newItemType.disabled = false;
          return newItemType;
        };
        const newItemTypes = prev.map(x => (x.productId === productId && x.itemTypeId === itemTypeId ? modifyItemType(x) : x));
        return newItemTypes;
      });
    },
    [productId]
  );

  const [addOrUpdateConventionItemType] = useMutation(ADD_UPDATE_INSURANCE_CONVENTION_ITEM_TYPE, {
    onError: ({ params }) => {
      updateLocalItemType(params.itemTypeId, 'disabled', false);
    }
  });

  const [deleteInsuranceConventionItemType] = useMutation(DELETE_INSURANCE_CONVENTION_ITEM_TYPE, {
    onError: ({ params }) => {
      updateLocalItemType(params.itemTypeId, 'disabled', false);
    }
  });

  const handleAdd = useCallback(() => {
    setItemTypes(prev => [{ isNew: true, key: crypto.randomUUID(), productId }, ...prev]);
    setInAdd(true);
  }, [productId]);

  const handleCancelAdd = useCallback(() => {
    setItemTypes(prev => prev.filter((_, i) => i !== 0));
    setInAdd(false);
  }, []);

  const handleAddOrUpdateItemType = useCallback(
    itemType => {
      if (itemTypes.some(x => x.productId === itemType.productId && x.itemTypeId === itemType.itemTypeId)) {
        addToast(t('InsuranceConventions.Details.ItemType.ItemTypeExists'), 'error');
        return;
      }
      updateLocalItemType(itemType.initialItemTypeId, 'disabled', true);

      // eslint-disable-next-line no-unused-vars
      const { isNew, key, editing, disabled, itemTypeName, ...sanitizedItemType } = itemType;

      addOrUpdateConventionItemType({
        variables: { input: sanitizedItemType },
        errorHandlingParams: { insuranceTypeId: itemType.initialItemTypeId }
      });
    },
    [itemTypes, updateLocalItemType, addOrUpdateConventionItemType, addToast, t]
  );

  const handleDeleteItemType = useCallback(
    (productId, itemTypeId) => {
      confirm(
        () => {
          updateLocalItemType(itemTypeId, 'disabled', true);
          deleteInsuranceConventionItemType({ variables: { productId, itemTypeId }, errorHandlingParams: { itemTypeId } });
        },
        {
          title: t('InsuranceConventions.Details.ItemType.DeleteItemType'),
          content: t('InsuranceConventions.Details.ItemType.AreYouSureDeleteItemType')
        }
      );
    },
    [confirm, deleteInsuranceConventionItemType, t, updateLocalItemType]
  );

  useSubscription(INSURANCE_CONVENTION_ITEM_TYPE_UPDATED, {
    onData: ({
      data: {
        data: {
          insuranceConventionItemTypeUpdated: { isNew, productId, initialItemTypeId, itemType }
        }
      }
    }) => {
      if (isNew) {
        handleCancelAdd();
        setItemTypes(prev => {
          const newItemTypes = [...prev, itemType];
          sortBy(newItemTypes, ['itemTypeId']);
          return newItemTypes;
        });
      } else {
        setItemTypes(prev => {
          const newItemTypes = prev.map(x =>
            x.productId === productId && x.itemTypeId === initialItemTypeId ? { ...itemType, editing: false, disabled: false } : x
          );
          sortBy(newItemTypes, ['itemTypeId']);
          return newItemTypes;
        });
      }
      addCachedDetail(
        client,
        { name: 'insuranceConventionItemTypes', value: INSURANCE_CONVENTION_ITEM_TYPES_QUERY },
        { productId },
        x => x.productId === productId && x.itemTypeId === initialItemTypeId,
        itemType,
        ['itemTypeId']
      );
      addToast(t('InsuranceConventions.Details.ItemType.ItemTypeSaved'), 'success');
    }
  });

  useSubscription(INSURANCE_CONVENTION_ITEM_TYPE_DELETED, {
    onData: ({
      data: {
        data: {
          insuranceConventionItemTypeDeleted: { productId, itemTypeId }
        }
      }
    }) => {
      setItemTypes(prev => prev.filter(x => !(x.productId === productId && x.itemTypeId === itemTypeId)));
      removeCachedDetail(
        client,
        { name: 'insuranceConventionItemTypes', value: INSURANCE_CONVENTION_ITEM_TYPES_QUERY },
        { productId },
        x => x.productId === productId && x.itemTypeId === itemTypeId,
        ['itemTypeId']
      );
      addToast(t('InsuranceConventions.Details.ItemType.ItemTypeDeleted'), 'success');
    }
  });

  const getItemTypeKey = useCallback(itemType => (itemType.isNew ? itemType.key : `${itemType.productId}_${itemType.itemTypeId}`), []);

  return (
    <>
      {isNewConvention ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
          </Box>
        </Grid>
      ) : (
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>{t('InsuranceConventions.Details.ItemType.Header')}</StyledHeaderCell>
              <StyledHeaderCell align='right'>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={inAdd || isNewConvention || !canWrite}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Add')}
                </Button>
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!isNewConvention && loading && (
                <TableCell colSpan={2}>
                  <FakeText lines={5} />
                </TableCell>
              )}
              {!isNewConvention && !loading && itemTypes.length === 0 && (
                <TableCell colSpan={2}>
                  <Box display='flex' alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {!isNewConvention &&
              !loading &&
              itemTypes.length > 0 &&
              itemTypes.map(itemType => (
                <InsuranceConventionItemTypeContainer
                  key={getItemTypeKey(itemType)}
                  itemType={itemType}
                  itemTypesDictionary={itemTypesDictionary}
                  onAddOrUpdate={handleAddOrUpdateItemType}
                  onUpdateLocalItemType={updateLocalItemType}
                  onDelete={handleDeleteItemType}
                  onCancelAdd={handleCancelAdd}
                />
              ))}
          </TableBody>
        </StyledTable>
      )}
    </>
  );
};

InsuranceConventionItemTypesContainer.propTypes = {
  productId: PropTypes.number
};

export default InsuranceConventionItemTypesContainer;
